import { useEffect, useRef, useState } from 'react';

import {
  formatVoiceDate,
  voiceProviderIsProduction,
  voiceProviderLabel,
  VOICE_STATUS_LABELS,
} from '../../context/VoiceContext';
import { useVoice } from '../../hooks/useVoice';
import { api } from '../../services/api';
import type { VoiceConsent, VoiceSampleMeta } from '../../types';

type Step = 'consent' | 'mic' | 'phrases' | 'processing' | 'done';

const VoiceEnrollment = () => {
  const {
    status,
    profile,
    isLoading,
    hasActiveTwin,
    isMock,
    error,
    refresh,
  } = useVoice();

  const [step, setStep] = useState<Step>('consent');
  const [language, setLanguage] = useState('nl');
  const [businessName, setBusinessName] = useState('');
  const [phrases, setPhrases] = useState<string[]>([]);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [consent, setConsent] = useState<VoiceConsent | null>(null);
  const [samples, setSamples] = useState<VoiceSampleMeta[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [reEnrollConfirm, setReEnrollConfirm] = useState(false);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);
  const startedAt = useRef(0);
  const getTime = () => (typeof performance !== 'undefined' ? performance.now() : 0);

  useEffect(() => {
    if (language) {
      void api.voiceScripts(language).then((result) => setPhrases(result.phrases)).catch(() => undefined);
    }
  }, [language]);

  const stopMeters = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLevel(0);
  };

  useEffect(() => () => stopMeters(), []);

  const startLevelMeter = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    streamRef.current = stream;
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = ((data[i] ?? 128) - 128) / 128;
        sum += v * v;
      }
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return stream;
  };

  const startRecording = async () => {
    setLocalError(null);
    try {
      const stream = await startLevelMeter();
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const durationMs = getTime() - startedAt.current;
        stopMeters();
        if (durationMs < 1500) {
          setLocalError('Opname te kort — spreek de volledige zin (min. 1,5s).');
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mime });
        const reader = new FileReader();
        reader.onload = async () => {
          const audioData = (reader.result as string).split(',')[1] ?? '';
          try {
            const meta = await api.voiceUploadSample({
              phraseId: `phrase_${phraseIdx}`,
              audioData,
              durationMs,
              sampleRate: 16000,
            });
            setSamples((prev) => [...prev, meta]);
            const next = phraseIdx + 1;
            if (next >= phrases.length) {
              setStep('processing');
              void finalizeEnrollment();
            } else {
              setPhraseIdx(next);
            }
          } catch {
            setLocalError('Sample upload mislukt.');
          }
        };
        reader.readAsDataURL(blob);
      };
      startedAt.current = getTime();
      rec.start();
      setRecording(true);
    } catch {
      setLocalError('Microfoon toegang geweigerd of niet beschikbaar.');
    }
  };

  const stopRecording = () => {
    setRecording(false);
    mediaRef.current?.stop();
  };

  const finalizeEnrollment = async () => {
    if (!consent || samples.length === 0) return;
    try {
      await api.voiceFinalizeEnrollment({
        consentId: consent.id,
        samples,
        languagePrimary: language,
        displayName: businessName || 'Owner',
        provider: 'mock',
        providerVoiceId: `mock_${crypto.randomUUID()}`,
      });
      setStep('done');
      await refresh();
    } catch {
      setLocalError('Enrollment finaliseren mislukt.');
      setStep('phrases');
    }
  };

  const acceptConsent = async () => {
    if (!checks.own || !checks.purposes || !checks.revoke) {
      setLocalError('Vink alle verplichte toestemmingsvakjes aan.');
      return;
    }
    try {
      const c = await api.voiceConsent({
        version: '2026-08-11-v2',
        retentionDays: 365,
        learningFromCall: checks.learning,
      });
      setConsent(c);
      setStep('mic');
      setLocalError(null);
    } catch {
      setLocalError('Toestemming kon niet worden opgeslagen.');
    }
  };

  const [checks, setChecks] = useState({ own: false, purposes: false, revoke: false, learning: false });

  const handleReenroll = async () => {
    if (!reEnrollConfirm) {
      setReEnrollConfirm(true);
      return;
    }
    if (!profile) return;
    try {
      await api.voiceDeleteProfile(profile.id);
      setReEnrollConfirm(false);
      setDeleteConfirm(false);
      setStep('consent');
      setSamples([]);
      setPhraseIdx(0);
      setConsent(null);
      await refresh();
    } catch {
      setLocalError('Voice Twin kon niet worden verwijderd. Probeer het opnieuw.');
      setReEnrollConfirm(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    if (!profile) return;
    try {
      await api.voiceDeleteProfile(profile.id);
      setDeleteConfirm(false);
      await refresh();
    } catch {
      setLocalError('Voice Twin kon niet worden verwijderd.');
      setDeleteConfirm(false);
    }
  };

  if (isLoading || status === 'idle') {
    return (
      <section className="voice-enrollment" aria-label="Voice Twin laden">
        <div className="voice-hydration">
          <span className="ai-spinner" />
          <p>Voice Twin-status laden…</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="voice-enrollment" aria-label="Voice Twin fout">
        <div className="voice-hydration">
          <p className="notice error" role="alert">
            {error.message}
          </p>
          {error.canRetry && (
            <button type="button" className="button primary" onClick={() => refresh()}>
              Opnieuw proberen
            </button>
          )}
        </div>
      </section>
    );
  }

  if (hasActiveTwin && profile) {
    return (
      <section className="voice-enrollment" aria-label="Voice Twin actief">
        <div className="voice-active-card">
          <div className="voice-active-header">
            <span className="voice-status-badge active">● Voice Twin Actief</span>
            {!voiceProviderIsProduction(profile.provider) && (
              <span className="voice-status-badge warning">Test / ontwikkel</span>
            )}
          </div>

          <h2>Je Voice Twin is klaar voor gebruik</h2>

          <dl className="voice-active-details">
            <div>
              <dt>Status</dt>
              <dd>{VOICE_STATUS_LABELS[profile.status]}</dd>
            </div>
            <div>
              <dt>Provider</dt>
              <dd>{voiceProviderLabel(profile.provider)}</dd>
            </div>
            <div>
              <dt>Taal</dt>
              <dd>{profile.languagePrimary}</dd>
            </div>
            <div>
              <dt>Versie</dt>
              <dd>v{profile.version}</dd>
            </div>
            {profile.consentAcceptedAt && (
              <div>
                <dt>Toestemming gegeven</dt>
                <dd>{formatVoiceDate(profile.consentAcceptedAt)}</dd>
              </div>
            )}
          </dl>

          <div className="voice-active-notice">
            <p>
              Ga naar <strong>Instellingen → Voice Agent</strong> om auto-antwoord in te schakelen.
            </p>
            {isMock && (
              <p className="notice warning small">
                Je gebruikt een Mock-provider. Dit is alleen bedoeld voor testen en ontwikkeling.
                Voor productie gebruik moet een echte voice-provider worden geconfigureerd.
              </p>
            )}
          </div>

          <div className="voice-active-actions">
            {reEnrollConfirm ? (
              <div className="voice-confirm danger">
                <p>Weet je zeker dat je je huidige Voice Twin wilt vervangen? Dit verwijdert het huidige stemmodel.</p>
                <div className="button-row">
                  <button type="button" className="button danger" onClick={() => void handleReenroll()}>
                    Ja, opnieuw enrollen
                  </button>
                  <button type="button" className="button secondary" onClick={() => setReEnrollConfirm(false)}>
                    Annuleren
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="button secondary" onClick={() => setReEnrollConfirm(true)}>
                Opnieuw enrollen
              </button>
            )}

            {deleteConfirm ? (
              <div className="voice-confirm danger">
                <p>Weet je zeker dat je je Voice Twin permanent wilt verwijderen? Dit wist alle stemvoorbeelden en het stemmodel.</p>
                <div className="button-row">
                  <button type="button" className="button danger" onClick={() => void handleDelete()}>
                    Ja, verwijderen
                  </button>
                  <button type="button" className="button secondary" onClick={() => setDeleteConfirm(false)}>
                    Annuleren
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="button danger" onClick={() => setDeleteConfirm(true)}>
                Verwijderen (GDPR)
              </button>
            )}
          </div>

          {localError && <p className="notice error small">{localError}</p>}
        </div>
      </section>
    );
  }

  return (
    <section className="voice-enrollment" aria-label="Voice enrollment">
      <header className="panel-header">
        <div>
          <p className="eyebrow">AI Voice</p>
          <h2>Voice Twin Enrollment</h2>
        </div>
      </header>

      <div className="enrollment-steps">
        <span className={`enrollment-step ${step === 'consent' ? 'active' : ''} ${step !== 'consent' ? 'done' : ''}`}>1. Toestemming</span>
        <span className={`enrollment-step ${step === 'mic' ? 'active' : ''} ${['phrases', 'processing', 'done'].includes(step) ? 'done' : ''}`}>2. Microfoon</span>
        <span className={`enrollment-step ${step === 'phrases' ? 'active' : ''} ${['processing', 'done'].includes(step) ? 'done' : ''}`}>3. Voorbeelden</span>
        <span className={`enrollment-step ${step === 'processing' ? 'active' : ''} ${step === 'done' ? 'done' : ''}`}>4. Klaar</span>
      </div>

      {localError && <p className="notice error small">{localError}</p>}

      {step === 'consent' && (
        <div className="enrollment-panel">
          <h3>Toestemming Voice Twin</h3>
          <p className="tool-description">
            We nemen korte zinnen op om een <strong>digitaal stemmodel</strong> van jou te maken.
            Dit is biometrische data, versleuteld opgeslagen op jouw server.
            De AI mag alleen antwoorden met jouw stem als jij auto-antwoord aanzet.
            Bellers horen dat het een AI is. Je kunt alles in één klik wissen.
          </p>
          <div className="consent-checks">
            {([
              ['own', 'Ik ben de persoon wiens stem wordt opgenomen.'],
              ['purposes', 'Ik geef toestemming voor clone + AI-beantwoording van gesprekken.'],
              ['revoke', 'Ik begrijp dat ik toestemming kan intrekken en data kan wissen.'],
            ] as const).map(([key, label]) => (
              <label key={key} className="consent-check">
                <input
                  type="checkbox"
                  checked={checks[key]}
                  onChange={(e) => setChecks((c) => ({ ...c, [key]: e.target.checked }))}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <p className="tool-description small">
            De onderstaande optie is niet verplicht, maar nodig als je wilt dat de AI
            ook leert van je gesprekken.
          </p>
          <div className="consent-checks learning-consent">
            <label className="consent-check">
              <input
                type="checkbox"
                checked={checks.learning}
                onChange={(e) => setChecks((c) => ({ ...c, learning: e.target.checked }))}
              />
              <span>
                Ik geef toestemming dat mijn gesprekken worden getranscribeerd, dat daaruit
                feiten worden geleerd voor de AI-assistent, dat de transcripties na 365 dagen
                worden verwijderd en dat ik deze toestemming altijd weer kan intrekken.
              </span>
            </label>
          </div>
          <div className="consent-options">
            <label className="tool-field">
              <span>Taal</span>
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </label>
            <label className="tool-field">
              <span>Bedrijfsnaam</span>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                maxLength={200}
                placeholder="Je bedrijfsnaam"
              />
            </label>
          </div>
          <button type="button" className="button primary" onClick={() => void acceptConsent()}>
            Akkoord — start enrollment
          </button>
        </div>
      )}

      {step === 'mic' && (
        <div className="enrollment-panel">
          <h3>Microfoon check</h3>
          <p className="tool-description">Gebruik een stille ruimte, spreek op 20-30 cm van de mic.</p>
          <div className="mic-level-bar">
            <div className="mic-level-fill" style={{ width: `${level * 100}%` }} />
          </div>
          <button type="button" className="button primary" onClick={() => setStep('phrases')}>
            Microfoon werkt — door naar voorbeelden
          </button>
          <button type="button" className="button secondary" onClick={() => void startLevelMeter()}>
            Test microfoon
          </button>
        </div>
      )}

      {step === 'phrases' && phrases.length > 0 && (
        <div className="enrollment-panel">
          <h3>Voorbeeld {phraseIdx + 1} van {phrases.length}</h3>
          <div className="phrase-display">
            <p className="phrase-text">{phrases[phraseIdx]}</p>
          </div>
          <div className="recording-controls">
            {!recording ? (
              <button type="button" className="button primary record-btn" onClick={() => void startRecording()}>
                🎤 Opnemen
              </button>
            ) : (
              <button type="button" className="button danger record-btn" onClick={stopRecording}>
                ⏹ Stoppen
              </button>
            )}
          </div>
          {recording && (
            <div className="recording-indicator">
              <span className="recording-dot" />
              <span>Opname bezig…</span>
            </div>
          )}
          {samples.length > 0 && (
            <p className="muted small">{samples.length} van {phrases.length} opnames klaar</p>
          )}
        </div>
      )}

      {step === 'processing' && (
        <div className="enrollment-panel">
          <h3>Stemmodel aanmaken…</h3>
          <div className="processing-indicator">
            <span className="ai-spinner" />
            <p>Je stemvoorbeelden worden verwerkt…</p>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="enrollment-panel voice-done">
          <h2>✅ Voice Twin Klaar!</h2>
          <p>Je digitale stem is succesvol aangemaakt.</p>
          <p>Ga naar <strong>Instellingen → Voice Agent</strong> om auto-antwoord in te schakelen.</p>
          <button type="button" className="button primary" onClick={() => void refresh()}>
            Doorgaan
          </button>
        </div>
      )}
    </section>
  );
};

export default VoiceEnrollment;
